import React, { useEffect, useMemo, useRef } from 'react'; // Added useEffect and useMemo
import { Card, CardBody, CardFooter, CardHeader, CardHeaderToolbar } from "../../../_metronic/_partials/controls";
import ReactJson from 'react-json-view'
import { TraceEvent } from "../api/BusinessTracing";
import { Spinner } from "react-bootstrap";
import DeliveryOptionsDetailsCard  from "./DeliveryOptionsDetailsCard";
import { Maximize } from 'lucide-react';
import { TraceLoadingAnimation } from './TraceLoadingAnimation';

type Props = {
    traceEvents: TraceEvent[] | null,
    onRefreshTrace: () => void,
    showTracesToggle: () => void
    isLoading: boolean,
    cardProps?: any;
    showTraces: boolean
}

const BusinessTracingJsonCard = ({ traceEvents, onRefreshTrace, showTracesToggle, isLoading, cardProps, showTraces }: Props) => {
    const hasInitialFetched = useRef(false);

    useEffect(() => {
        if (!isLoading && (!traceEvents || traceEvents.length === 0) && !hasInitialFetched.current) {
            onRefreshTrace();
            hasInitialFetched.current = true;
        }
    }, [traceEvents, isLoading, onRefreshTrace]);

    const simplifiedTraceEvents = useMemo(() => {
        if (!traceEvents) return [];

        const mapped = traceEvents.map(event => ({
            name: event.name,
            source: event.source,
            publishedAt: event.publishedAt,
            tags: event.tags,
            data: event.data,
            fullTraceEvent: event
        }));

        return mapped.sort((left: any, right: any) => {
            const leftDate = new Date(left.fullTraceEvent.publishedAt).valueOf();
            const rightDate = new Date(right.fullTraceEvent.publishedAt).valueOf();
            return leftDate - rightDate;
        });
    }, [traceEvents]);

    return (
        <Card {...cardProps}>            
            <CardHeader title={"Details"} {...cardProps}>
                <CardFooter {...cardProps}>                    
                    {
                        <div className="d-flex justify-content-end">
                            <button
                                onClick={onRefreshTrace}
                                type="button"
                                className="btn btn-success"
                                disabled={isLoading}
                            >
                                {isLoading ? "Refreshing..." : "Refresh Details"}
                            </button>
                        </div>
                    }
                </CardFooter>
            </CardHeader>

            <CardBody {...cardProps}>
                {isLoading ? (
                    <TraceLoadingAnimation  displayText={simplifiedTraceEvents?.length?.toString()} subTitle={`Gathering ${simplifiedTraceEvents.length} events...`} />                    
                ) : simplifiedTraceEvents.length > 0 ? (
                    <DeliveryOptionsDetailsCard debugJson={simplifiedTraceEvents}/>
                ) : (
                    <div className="text-left p-5 alert-info rounded">
                        <p>No detailed information has been collected yet</p>
                        <div>It should be added automatically after a <b>Get Options</b> request has completed</div>
                    </div>
                )}
            </CardBody>
            
            <CardHeader title={showTraces === false ? "" : "DELIVERY OPTIONS TRACE"} {...cardProps}>
                <CardHeaderToolbar {...cardProps}>
                    <button
                        type="button"
                        className="border-0 bg-transparent"
                        onClick={() => { showTracesToggle() }}
                    >
                        {showTraces === false ? <u>Show Trace</u> : <u>Hide Trace</u>}
                    </button>
                </CardHeaderToolbar>
            </CardHeader>
            
            {showTraces &&
                <CardFooter {...cardProps}>
                    
                        <div className="d-flex justify-content-end">
                            <button
                                onClick={onRefreshTrace}
                                type="button"
                                className="btn btn-success"
                                disabled={isLoading}
                            >
                                {isLoading ? "Refreshing..." : "Refresh Trace"}
                            </button>
                        </div>
                    
                </CardFooter>
            }
            {showTraces && (
                <CardBody {...cardProps}>
                    {isLoading && simplifiedTraceEvents.length === 0 && (
                        <div className="text-center mt-5">
                            <Spinner animation="border" role="status" variant="primary"></Spinner>
                            <div>Calculating...</div>
                        </div>
                    )}
                    
                    {!traceEvents && !isLoading && (
                        <div className="text-left p-5 alert-info rounded">
                            <p>No tracing information has been collected yet</p>
                            <div>It should be added automatically after a <b>Get Options</b> request has completed</div>
                        </div>
                    )}
                    
                    {traceEvents && (
                        <ReactJson 
                            src={simplifiedTraceEvents} 
                            theme="shapeshifter:inverted" 
                            name="TraceEvents" 
                            displayDataTypes={false} 
                            displayObjectSize={false} 
                            collapsed={2} 
                        />
                    )}
                </CardBody>
            )}
            {showTraces &&
                <CardFooter {...cardProps}>
                    
                        <div className="d-flex justify-content-end">
                            <button
                                onClick={onRefreshTrace}
                                type="button"
                                className="btn btn-success"
                                disabled={isLoading}
                            >
                                {isLoading ? "Refreshing..." : "Refresh Trace"}
                            </button>
                        </div>
                
                </CardFooter>
            }
        </Card >
    )
}

export default BusinessTracingJsonCard;